import * as z from "zod";
import { changedTeamPatchPaths } from "../grouped-settings-changes";
import {
  packageProtectionPricingFromSettings,
  PackageProtectionPricingSchema,
  type PackageProtectionPricingSource,
} from "../package-protection/package-protection-pricing";
import {
  diffSettingsGroups,
  mergeSettingsGroups,
  type SettingsGroups,
} from "../tab-settings/settings-group-diff";

export const ReturnsCoverageSettingsSchema = z.object({
  returnCoverage: z.object({
    excludeProductTags: z.array(z.string()),
    excludeCollections: z.array(z.string()),
    excludeCustomerTags: z.array(z.string()),
    excludeProperties: z.array(z.string()),
    excludeVariantMetafields: z.array(z.string()),
  }),
  packageProtection: PackageProtectionPricingSchema,
});

export type ReturnsCoverageSettings = z.infer<
  typeof ReturnsCoverageSettingsSchema
>;

/**
 * Pricing is read-only on this tab, so it is deliberately absent: the endpoint
 * must not accept `packageProtection` at all. Those paths are admin-only under
 * Package Protection Plus and the legacy coverage page is the only writer that
 * enforces that, along with the bounds Mongoose only applies on `save()`.
 */
export const ReturnsCoverageSettingsChangesSchema = z.object({
  returnCoverage: ReturnsCoverageSettingsSchema.shape.returnCoverage
    .partial()
    .optional(),
});

export type ReturnsCoverageSettingsChanges = z.infer<
  typeof ReturnsCoverageSettingsChangesSchema
>;

export interface ReturnsCoverageTeamPatch {
  /** Dotted paths relative to `team.settings`. */
  set: Record<string, unknown>;
  unset: string[];
}

const SETTINGS_GROUP_KEYS = ["returnCoverage"];

/** The slice of team settings the Coverage tab reads. */
export interface ReturnsCoverageSettingsSource {
  settings: {
    countriesPackageProtection: string[];
    coverageExcludeProductTags: string[];
    coverageExcludeCollections: string[];
    coverageExcludeCustomerTags: string[];
    coverageExcludeProperties: string[];
    packageProtection?: PackageProtectionPricingSource | null;
    returns?: { coverageExcludedVariantMetafields?: string[] | null } | null;
  };
}

function settingsGroups(settings: ReturnsCoverageSettings): SettingsGroups {
  return { returnCoverage: settings.returnCoverage };
}

export function returnsCoverageSettingsFromTeam({
  settings,
}: ReturnsCoverageSettingsSource): ReturnsCoverageSettings {
  return {
    returnCoverage: {
      excludeProductTags: settings.coverageExcludeProductTags,
      excludeCollections: settings.coverageExcludeCollections,
      excludeCustomerTags: settings.coverageExcludeCustomerTags,
      excludeProperties: settings.coverageExcludeProperties,
      excludeVariantMetafields:
        settings.returns?.coverageExcludedVariantMetafields ?? [],
    },
    packageProtection: packageProtectionPricingFromSettings({
      packageProtection: settings.packageProtection,
      countriesPackageProtection: settings.countriesPackageProtection,
    }),
  };
}

export function returnsCoverageSettingsChanges(
  initial: ReturnsCoverageSettings,
  next: ReturnsCoverageSettings,
): ReturnsCoverageSettingsChanges {
  return ReturnsCoverageSettingsChangesSchema.parse(
    diffSettingsGroups(
      SETTINGS_GROUP_KEYS,
      settingsGroups(initial),
      settingsGroups(next),
    ),
  );
}

function applyChanges(
  base: ReturnsCoverageSettings,
  changes: ReturnsCoverageSettingsChanges,
): ReturnsCoverageSettings {
  return ReturnsCoverageSettingsSchema.parse({
    ...mergeSettingsGroups(SETTINGS_GROUP_KEYS, settingsGroups(base), changes),
    packageProtection: base.packageProtection,
  });
}

export function returnsCoverageSettingsToTeamPatch(
  next: ReturnsCoverageSettings,
): ReturnsCoverageTeamPatch {
  const set: Record<string, unknown> = {
    coverageExcludeProductTags: next.returnCoverage.excludeProductTags,
    coverageExcludeCollections: next.returnCoverage.excludeCollections,
    coverageExcludeCustomerTags: next.returnCoverage.excludeCustomerTags,
    coverageExcludeProperties: next.returnCoverage.excludeProperties,
    "returns.coverageExcludedVariantMetafields":
      next.returnCoverage.excludeVariantMetafields,
  };

  return { set, unset: [] };
}

export function returnsCoverageTeamPatchForChanges(
  current: ReturnsCoverageSettingsSource,
  changes: ReturnsCoverageSettingsChanges,
): ReturnsCoverageTeamPatch {
  const stored = returnsCoverageSettingsFromTeam(current);
  const baseline = returnsCoverageSettingsToTeamPatch(stored);
  const requested = returnsCoverageSettingsToTeamPatch(
    applyChanges(stored, changes),
  );

  return changedTeamPatchPaths(baseline, requested);
}
